import dayjs from 'dayjs'
import useUsersList from './useUsersList'
import { ResidentDtos } from '@lmc/ts-common'
import { useMemo } from 'react'
import {
  getTransformedEventUsers,
  getTransformedEventResidents,
  getInvalidEventsFromAbsences,
  getTransformedUserEventsData,
  getTransformedResidentEventsData,
  getTransformedEventShifts,
} from './utils'
import useRecursiveInstancesList from './useRecursiveInstancesList'
import { RotaBuilderFiltersType } from 'screens/Rostering/types'
import useResidentsList from './useResidentsList'
import { getDateWithoutUTC, YEAR_FIRST_DATE_FORMAT } from '../utils.datetime'
import { useAbsencesList } from './useAbsencesList'
import { useAbsenceTypesList } from './useAbsenceTypesList'

type UseRosterPublishedProps = {
  selectedDate: Date
  intervalType: 'daily' | 'weekly'
  filterParams: RotaBuilderFiltersType
  totalEvents?: number
  recursionEnabled?: boolean
}

export const useRosterPublished = ({
  selectedDate,
  intervalType,
  filterParams,
  totalEvents,
  recursionEnabled,
}: UseRosterPublishedProps) => {
  // TODO connect filters from useRoster
  const {
    instances,
    isLoading: isInstancesLoading,
    metadata,
  } = useRecursiveInstancesList({
    selectedDate,
    intervalType,
    otherFilterParams: {
      status: filterParams.status,
      allocationStatuses: filterParams.allocations,
      teamMembers: filterParams.teamMembers,
      serviceUsers: filterParams.serviceUsers,
      instanceTypes: filterParams.typeOfEvent,
      ...(filterParams.siteShift && { siteShift: true }),
    },
    totalEvents,
    recursionEnabled,
  })

  const { residents, isLoading: isResidentsListLoading } = useResidentsList({
    filters: {
      residentIds: filterParams.serviceUsers,
      date: getDateWithoutUTC(selectedDate, YEAR_FIRST_DATE_FORMAT),
      type: 'Instances',
    },
  })

  const { users, isLoading: isUsersListLoading } = useUsersList({
    selectedDate,
    type: 'Instances',

    otherFilterParams: {
      absences: filterParams.absences,
      roles: filterParams.roles,
      overtime: filterParams.overtime,
      userIds: filterParams?.teamMembers,
    },
  })

  const date =
    intervalType === 'weekly'
      ? dayjs(selectedDate).startOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)
      : dayjs(selectedDate).format(YEAR_FIRST_DATE_FORMAT)

  const startDate = dayjs(date).startOf('day').toISOString()

  const endDate =
    intervalType === 'weekly'
      ? dayjs(date).endOf('isoWeek').toISOString()
      : dayjs(date).endOf('day').toISOString()

  const { absences } = useAbsencesList({
    startDate,
    endDate,
  })

  const { absenceTypes } = useAbsenceTypesList()

  const usersEvents = useMemo(
    () => getTransformedUserEventsData(instances),
    [instances],
  )

  const residentsEvents = useMemo(
    () => getTransformedResidentEventsData(instances),
    [instances],
  )

  const shifts = useMemo(
    () => getTransformedEventShifts(instances),
    [instances],
  )

  return {
    shifts,
    users: getTransformedEventUsers(users),
    usersEvents: usersEvents || [],
    residents: getTransformedEventResidents(
      residents as unknown as ResidentDtos[],
      residentsEvents,
    ),
    residentsEvents: residentsEvents || [],
    isPublishedRotaLoading:
      isInstancesLoading || isResidentsListLoading || isUsersListLoading,
    invalid: getInvalidEventsFromAbsences(absences, absenceTypes),
    metadata,
    hasEvents: instances?.length > 0,
  }
}

export default useRosterPublished
